import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import ModalPortal from './ModalPortal';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'secondary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  icon?: React.ReactNode;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  onConfirm, 
  onCancel,
  icon = <AlertTriangle className="w-5 h-5 text-amber-500" />
}) => {
  if (!isOpen) return null;

  // Determine button style based on variant
  const getButtonClass = () => {
    switch (confirmVariant) {
      case 'danger':
        return '!bg-red-500 hover:!bg-red-600 !border-red-500 !shadow-red-500/30';
      case 'secondary':
        return '!bg-amber-500 hover:!bg-amber-500 !border-amber-500 !shadow-amber-500/30';
      case 'primary':
      default:
        return '';
    }
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in confirmation-modal">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border border-amber-500/20 animate-fade-in-up">
          <div className="p-5 border-b border-amber-500/20">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-amber-500/10 mr-3">
                {icon}
              </div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                {title}
              </h3>
            </div>
          </div>
          <div className="p-5">
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">
              {message}
            </p>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={onCancel}
                className="w-full sm:w-auto"
              >
                {cancelText}
              </Button>
              <Button 
                variant={confirmVariant} 
                onClick={onConfirm}
                className={`w-full sm:w-auto ${getButtonClass()}`}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ConfirmationModal; 